"use client"

import { useEffect, useState } from "react"
import { createRoot } from "react-dom/client"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { icon } from "./svgs"

/**
 * Displays a modal feedback dialog with a title, description, and custom buttons.
 * The modal can be dismissed and optionally displays an icon based on the iconType prop.
 * @param {string} modalTitle - The title to be displayed in the modal.
 * @param {string} modalDescription - The description to be displayed in the modal.
 * @param {Array} buttons - Array of button objects containing content, action, and type for each button.
 * @param {boolean} dismissible - If true, the modal can be dismissed by clicking the "Cerrar" button.
 * @param {string} iconType - Specifies which icon to display (e.g., 'question', 'success', 'error', 'warning').
 */
const modalFeedbackReact = (modalTitle, modalDescription, buttons, dismissible = true, iconType = false) => {
  const modalContainer = document.createElement("div")
  document.body.appendChild(modalContainer)

  // Close modal function
  const closeModal = () => {
    setTimeout(() => {
      root.unmount()
      document.body.removeChild(modalContainer)
    }, 0)
  }

  const ModalComponent = () => {
    const [open, setOpen] = useState(true)

    useEffect(() => {
      if (!open) closeModal()
    }, [open])

    // Define button variants based on type
    const getButtonVariant = (type) => {
      switch (type) {
        case "dark":
          return "default"
        case "ligth":
          return "secondary"
        case "info":
          return "outline"
        case "warning":
          return "warning"
        case "success":
          return "success"
        case "error":
          return "destructive"
        default:
          return "outline"
      }
    }

    // Define custom button classes for additional styling
    const getButtonClass = (type) => {
      switch (type) {
        case "info":
          return "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
        case "warning":
          return "border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100"
        case "success":
          return "border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
        case "error":
          return "border-red-200 bg-red-50 text-red-800 hover:bg-red-100"
        default:
          return ""
      }
    }

    return (
      <Dialog open={open} onOpenChange={(open) => (dismissible ? setOpen(open) : null)}>
        <DialogContent className=" max-w-xs sm:max-w-md">
          {iconType && <div className="absolute top-2 right-10">{icon[iconType]}</div>}
          <DialogTitle className="text-xl font-semibold">{modalTitle}</DialogTitle>
          <DialogDescription className="text-base font-medium">{modalDescription}</DialogDescription>
          <DialogFooter className="flex flex-wrap justify-end gap-4 mt-6">
            {dismissible && (
              <Button variant="outline" onClick={closeModal} className="h-10 px-3.5 text-base font-semibold">
                Cerrar
              </Button>
            )}
            {buttons &&
              buttons.map(({ content, action, type }) => (
                <Button
                  key={content}
                  onClick={() => {
                    action()
                    closeModal()
                  }}
                  variant={getButtonVariant(type)}
                  className={`h-10 px-3.5 text-base font-semibold ${getButtonClass(type)}`}
                >
                  {content}
                </Button>
              ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const root = createRoot(modalContainer)
  root.render(<ModalComponent />)
}

export default modalFeedbackReact

